/**
 * Short-lived signed URLs for a report's two documents (worker-generated — see
 * ../api.ts#fetchDocumentUrls). Refreshes itself shortly before the URLs expire so a long
 * reading session in the workspace never hits a dead link.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError, fetchDocumentUrls, type DocumentUrls } from '../api';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; urls: DocumentUrls };

const REFRESH_MARGIN_SECONDS = 120;

export function useDocumentUrls(reportId: string): State {
  const [state, setState] = useState<State>({ status: 'loading' });
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const urls = await fetchDocumentUrls(reportId);
        if (cancelled) return;
        setState({ status: 'ready', urls });
        const delayMs = Math.max(10, urls.expiresInSeconds - REFRESH_MARGIN_SECONDS) * 1000;
        timerRef.current = window.setTimeout(load, delayMs);
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'Could not load the documents for this report.';
        setState({ status: 'error', message });
      }
    }

    setState({ status: 'loading' });
    void load();
    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [reportId]);

  return state;
}
